import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
  Users, Newspaper, Image, MessageSquare, 
  TrendingUp, ArrowRight, Clock, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentNews, setRecentNews] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, newsRes, contactsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/news/all`, { headers: getAuthHeaders() }),
          axios.get(`${API}/contacts`, { headers: getAuthHeaders() })
        ]);
        
        setStats(statsRes.data);
        setRecentNews(newsRes.data.slice(0, 5));
        setRecentContacts(contactsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
      setLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statCards = [
    { icon: Users, label: 'Artistas', value: stats?.artists || 0, color: 'from-[#00E5FF]', link: '/admin/artistas' },
    { icon: Newspaper, label: 'Notícias', value: stats?.news || 0, color: 'from-[#0A84FF]', link: '/admin/noticias' },
    { icon: Image, label: 'Galeria', value: stats?.gallery || 0, color: 'from-purple-500', link: '/admin/galeria' },
    { icon: MessageSquare, label: 'Contatos', value: stats?.contacts || 0, color: 'from-green-500', link: '/admin/contatos', badge: stats?.pending_contacts },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-gray-400">Bem-vindo ao painel administrativo da Ritmo</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6 hover:border-white/10 transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} to-transparent flex items-center justify-center`}>
                  <Icon size={24} className="text-white" />
                </div>
                {stat.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {stat.badge} pendentes
                  </span>
                )}
              </div>
              <h3 className="text-3xl font-bold text-white mb-1">{stat.value}</h3>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                {stat.label}
                <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </p>
            </Link>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent News */}
        <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white">Últimas Notícias</h2>
            <Link to="/admin/noticias" className="text-[#00E5FF] text-sm hover:underline">
              Ver todas →
            </Link>
          </div>
          <div className="space-y-4">
            {recentNews.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhuma notícia cadastrada</p>
            ) : (
              recentNews.map((news) => (
                <div key={news.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors">
                  <img 
                    src={news.image} 
                    alt={news.title}
                    className="w-16 h-12 rounded object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white text-sm font-medium truncate">{news.title}</h4>
                    <p className="text-gray-500 text-xs flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-[#0A84FF]/20 text-[#0A84FF] rounded text-xs">
                        {news.category}
                      </span>
                      {!news.published && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-500 rounded text-xs">
                          Rascunho
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Contacts */}
        <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white">Últimos Contatos</h2>
            <Link to="/admin/contatos" className="text-[#00E5FF] text-sm hover:underline">
              Ver todos →
            </Link>
          </div>
          <div className="space-y-4">
            {recentContacts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Nenhum contato recebido</p>
            ) : (
              recentContacts.map((contact) => (
                <div key={contact.id} className="p-3 rounded-lg hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-white text-sm font-medium">{contact.name}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      contact.status === 'pending' 
                        ? 'bg-yellow-500/20 text-yellow-500' 
                        : 'bg-green-500/20 text-green-500'
                    }`}>
                      {contact.status === 'pending' ? 'Pendente' : 'Respondido'}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs truncate">{contact.subject}</p>
                  <p className="text-gray-600 text-xs mt-1">{contact.email}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Ações Rápidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/admin/noticias?action=new"
            className="p-4 border border-dashed border-white/10 rounded-lg text-center hover:border-[#00E5FF] hover:bg-[#00E5FF]/5 transition-all"
          >
            <Newspaper className="mx-auto mb-2 text-[#00E5FF]" size={24} />
            <span className="text-white text-sm">Nova Notícia</span>
          </Link>
          <Link
            to="/admin/galeria?action=new"
            className="p-4 border border-dashed border-white/10 rounded-lg text-center hover:border-[#0A84FF] hover:bg-[#0A84FF]/5 transition-all"
          >
            <Image className="mx-auto mb-2 text-[#0A84FF]" size={24} />
            <span className="text-white text-sm">Nova Foto/Vídeo</span>
          </Link>
          <Link
            to="/admin/artistas?action=new"
            className="p-4 border border-dashed border-white/10 rounded-lg text-center hover:border-purple-500 hover:bg-purple-500/5 transition-all"
          >
            <Users className="mx-auto mb-2 text-purple-500" size={24} />
            <span className="text-white text-sm">Novo Artista</span>
          </Link>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-4 border border-dashed border-white/10 rounded-lg text-center hover:border-green-500 hover:bg-green-500/5 transition-all"
          >
            <Eye className="mx-auto mb-2 text-green-500" size={24} />
            <span className="text-white text-sm">Ver Site</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
